import { z } from 'zod';
import { createOffsetPaginatedSchema, OffsetPaginationParamsSchema, } from '../offset-pagination.js';
import { TRIAL_VISIT_TYPES } from '../trials/trials.schema.js';
// ---------------------------------------------------------------------------
// Catalogs
// ---------------------------------------------------------------------------
/**
 * The nine fixed participation statuses (REQ-PAT-014, "dziewięć stałych
 * statusów"). Operational users must not extend the list.
 *
 * DELIBERATE duplication of `ENROLLMENT_STATUSES` in `@ctms/db` (decision of
 * 2026-09-27), following the existing pattern of `TRIAL_VISIT_KINDS` and
 * `PERMISSION_KEYS`: `@ctms/contracts` stays free of a dependency on the
 * schema package, and the database keeps its own CHECK constraint on the same
 * nine values anyway. If one list changes, both must change.
 *
 * The active/terminal split (Q-PAT-006: `pre_screening`, `screened`,
 * `ongoing`, `follow_up` block a second concurrent participation) lives in the
 * service, not here — this catalog carries all nine.
 */
export const ENROLLMENT_STATUSES = {
    PRE_SCREENING: 'pre_screening',
    PRE_SCREENING_FAILURE: 'pre_screening_failure',
    SCREENED: 'screened',
    SCREENING_FAILURE: 'screening_failure',
    ONGOING: 'ongoing',
    COMPLETED: 'completed',
    WITHDRAWN: 'withdrawn',
    FOLLOW_UP: 'follow_up',
    LOST_TO_FOLLOW_UP: 'lost_to_follow_up',
};
export const EnrollmentStatusSchema = z.enum(Object.values(ENROLLMENT_STATUSES));
/**
 * Kit registration flag of a scheduled visit (legacy `Patient_visit.Flag`;
 * mirrors the `patient_visits_flag_valid` CHECK in migration 0005).
 * W: white, R: red, G: green.
 */
export const PATIENT_VISIT_FLAGS = {
    WHITE: 'W',
    RED: 'R',
    GREEN: 'G',
};
export const PatientVisitFlagSchema = z.enum(Object.values(PATIENT_VISIT_FLAGS));
// ---------------------------------------------------------------------------
// Participation in a trial (udział pacjenta w badaniu, REQ-PAT-009…017)
// ---------------------------------------------------------------------------
export const TrialEnrollmentDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    patientId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    /** REQ-PAT-011: required, unique within (tenant_id, trial_id) — Q-PAT-014. */
    screeningNumber: z.string().min(1).max(64),
    /** REQ-PAT-012: optional second number; absent means `null`. */
    randomizationNumber: z.string().max(64).nullable(),
    /** REQ-PAT-013: frozen at entry, never recomputed from the current age. */
    adult: z.boolean(),
    status: EnrollmentStatusSchema,
    /** REQ-PAT-015: the person signed the travel-cost consent. */
    travelCosts: z.boolean(),
    /** REQ-PAT-016: the person is on pregnancy tests per protocol. */
    pregnancyTest: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * One row of a participant's visit schedule (REQ-PAT-024/025).
 *
 * A row is EITHER a protocol-catalog position (`trialVisitId` set, pointing at
 * Domain 2's `trial_visits`) OR a #41 template-visit preset (`presetName` set —
 * UNS1–UNS5, SCREENING FAILURE, OUT; not part of the protocol catalog). The
 * XOR is DB-enforced (CHECK `patient_visits_catalog_xor_preset`, migration
 * 0027) and NOT repeated as a Zod refinement so the schema stays a plain
 * object for `createZodDto` consumers. Dates are calendar days (`date` in the
 * schema), not instants — hence `z.iso.date()`.
 */
export const PatientVisitDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    enrollmentId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    /** Catalog position; null on a #41 preset row. */
    trialVisitId: z.number().int().positive().nullable(),
    /** #41 template preset name (varchar(64)); null on a catalog row. */
    presetName: z.string().max(64).nullable(),
    /** Empty until Cockpit (D4) schedules the visit — REQ-PAT-024. */
    dateScheduled: z.iso.date().nullable(),
    timeScheduled: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable(),
    dateReal: z.iso.date().nullable(),
    /** REQ-VIS-020: performed hour, `HH:MM`; empty for phone visits. */
    timeReal: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable(),
    /** Contact channel copied from the catalog position (legacy S/P/H/I). */
    type: z.enum(Object.values(TRIAL_VISIT_TYPES)),
    flag: PatientVisitFlagSchema.nullable(),
    mainComment: z.string().nullable(),
    confirmedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Enrolling a site-chart person into a trial (REQ-PAT-009…014).
 *
 * `patientId` sits in the URL path (the chart is the resource the caller
 * already has on screen); the target trial comes in the body, because one
 * person joins many trials over time (REQ-PAT-009). `travelCosts` /
 * `pregnancyTest` are optional and fall back to `false`.
 */
export const EnrollPatientRequestSchema = z.object({
    trialId: z.number().int().positive(),
    /** REQ-PAT-011: required, stored trimmed. */
    screeningNumber: z.string().trim().min(1).max(64),
    /** REQ-PAT-012: optional; blank counts as absent (service normalizes). */
    randomizationNumber: z.string().trim().max(64).nullable().optional(),
    travelCosts: z.boolean().optional(),
    pregnancyTest: z.boolean().optional(),
});
/**
 * The created participation together with the qualification-stage plan
 * generated from the protocol catalog (REQ-PAT-025). The plan holds the rows
 * created by this call; a protocol without qualification positions yields an
 * empty plan rather than an error.
 */
export const EnrollPatientResponseSchema = z.object({
    enrollment: TrialEnrollmentDtoSchema,
    plan: z.array(PatientVisitDtoSchema),
});
/**
 * Manual participation status change (`PATCH
 * /patients/:patientId/enrollments/:enrollmentId`, #7 B3).
 *
 * "prosty wybór statusu, bez żadnych powiązanych akcji i konsekwencji"
 * (komentarz Piotra #7 z 2026-10-25): the dropdown presents the full constant
 * dictionary of nine statuses (REQ-PAT-014), the system validates NO
 * transitions and triggers NO side effects — the responsibility for the
 * change is the explicit decision of the site staff (Q-PAT-008). This is also
 * the D4 unlock path: a manual return of the status to `pre_screening` makes
 * the participation deletable again.
 */
export const UpdateEnrollmentStatusRequestSchema = z.object({
    status: EnrollmentStatusSchema,
});
/**
 * Trial display data of one participation (#7 B2) — the same subset the
 * cockpit bars expose (`TrialBarItemDto`) plus the formal protocol number,
 * read through `TrialsService.getTrialsByIds` (never a cross-cluster SQL
 * join). Nullable defensively: `getTrialsByIds` silently omits ids invisible
 * to the tenant, and the summary row must still render without them.
 */
export const EnrollmentTrialDtoSchema = z.object({
    id: z.number().int().positive(),
    shortcut: z.string().min(1).max(20),
    shortTitle: z.string().min(1).max(255),
    protocolNumber: z.string().min(1).max(100),
    siteNumber: z.string().min(1).max(50),
});
/**
 * One participation together with its own visit schedule — the shape the
 * patient chart needs on entry (elements 6 and 9 of the Cockpit legend: the
 * chart bar plus the visit table). The enroll response keeps the bare pair;
 * the chart read extends it into the participation summary ("wszystkie
 * badania" of the 2026-10-25 batch, #7 B2).
 *
 * Span fields: the effective date of a visit is `dateReal ?? dateScheduled`;
 * `firstVisitDate` / `lastVisitDate` are the min/max effective date over
 * `plan`, and `durationDays` = last − first in calendar days (a single-day
 * participation spans 0). All three are null when no visit carries a date.
 */
export const TrialEnrollmentWithPlanDtoSchema = z.object({
    enrollment: TrialEnrollmentDtoSchema,
    plan: z.array(PatientVisitDtoSchema),
    trial: EnrollmentTrialDtoSchema.nullable(),
    firstVisitDate: z.iso.date().nullable(),
    lastVisitDate: z.iso.date().nullable(),
    durationDays: z.number().int().nonnegative().nullable(),
});
/**
 * The whole participation history of one person (`GET
 * /patients/:patientId/enrollments`), each with its schedule nested.
 *
 * `items` rather than a bare array: one person joins many trials over time, so
 * the response has room to grow (counts, filters) without breaking the item
 * contract. The nested plan keeps it to a single round trip for the chart.
 */
export const PatientEnrollmentsDtoSchema = z.object({
    items: z.array(TrialEnrollmentWithPlanDtoSchema),
});
// ---------------------------------------------------------------------------
// Participants of one trial (lista pacjentów w badaniu, REQ-VIS-006)
// ---------------------------------------------------------------------------
/**
 * The person behind a participation, cut down to what the "Pacjenci w badaniu"
 * list shows (element 5 of the Cockpit legend: Numer | Pacjent | Status).
 *
 * DELIBERATELY minimal — not the full `PatientDto`: the participant list is
 * paginated data of many people, so it carries no PESEL, address, admission
 * book number or notes. A list of many people is exactly where unnecessary
 * personal data would leak (rule 85-security-secrets, RODO). The chart itself
 * (element 6) keeps reading the full `PatientDto` through `GET /patients/:id`.
 */
export const TrialParticipantPatientDtoSchema = z.object({
    id: z.number().int().positive(),
    firstName: z.string().min(1).max(255),
    lastName: z.string().min(1).max(255),
});
/**
 * One row of the participant list of a trial: the participation (numbers +
 * status) plus the person it belongs to (REQ-VIS-006, P-11: both numbers
 * visible and searchable, the screening one shown first).
 */
export const TrialParticipantDtoSchema = z.object({
    enrollment: TrialEnrollmentDtoSchema,
    patient: TrialParticipantPatientDtoSchema,
});
/**
 * The offset envelope of the participant list (`GET
 * /trials/:trialId/enrollments`). Shape mirrors the other list endpoints of
 * this repo (`{ items, total, page, limit, hasNext }`, never `data`).
 */
export const TrialParticipantsDtoSchema = createOffsetPaginatedSchema(TrialParticipantDtoSchema);
/**
 * Sort keys of the participant list (tor C of the 2026-10-10 batch): the four
 * columns the coordinator orders by from the sort menu — name, the two numbers
 * and the status. The list renders a single "Numer" column that JOINS both
 * numbers, so the two number keys stay separate here; the menu maps 1:1 to a
 * key. Order is always ascending (A..Z / 0..9) — the source says "A-Z".
 */
export const TrialParticipantsSortSchema = z.enum([
    'lastName',
    'screeningNumber',
    'randomizationNumber',
    'status',
]);
/**
 * Query of the participant list: the shared offset pagination plus the status
 * filter (element 11 of the Cockpit legend) and one free-text search over the
 * person's name and BOTH numbers (P-11). The status set is the nine fixed
 * enrollment statuses — no other filtering exists on this list today.
 *
 * `sort` (tor C) is optional and absent means the default order (newest
 * participation first); the list is paginated server-side, so the ordering must
 * run in the database to cover the whole set, not just the current page.
 */
export const TrialParticipantsQuerySchema = OffsetPaginationParamsSchema.extend({
    search: z.string().trim().max(100).optional(),
    status: EnrollmentStatusSchema.optional(),
    sort: TrialParticipantsSortSchema.optional(),
});
//# sourceMappingURL=enrollments.schema.js.map